import { Expose, Transform } from 'class-transformer';
import { Point } from 'geojson';

import { AlertsPipelineModeType } from '@lib/axon/services/alerts-service-mode.types';
import { Default, ExposeRawData } from '@lib/common/decorators';
import { SnModel } from '@lib/common/models';
import { LiveUpdateServiceType } from 'libs/transport/live-update-switch/src/lib/live-update-switch.types';

import { FsGeospatialProvider } from './geospatial-provider.enum';
import { PresenceDataModeType } from './presence-data-mode.types';

export class SnOrganizationSettings extends SnModel {
  @Expose() @ExposeRawData() public constants: any;

  @Expose() public timezone: string;

  @Expose() public address: string;

  @Expose() public city: string;

  // Is this correct? ZIP codes can be more than numbers and come back from the API as strings
  @Expose() public zip: number;

  @Expose() public state: string;

  @Expose() public type: any;

  @Expose() public alertsBoundary: any;

  @Expose()
  public childOrganizations: string[];

  @Expose() public enableCommunicationServices: boolean;

  @Expose() public userPhoneNumberRequired: boolean;

  @Expose() public audioBitrate: number;
  @Expose() public audioChannels: number;
  @Expose() public audioSamplerate: number;
  @Expose()
  @Transform((value) => {
    return typeof value === 'string' ? parseFloat(value) : value;
  })
  public autoDockRadius: number;
  @Expose() public autoDockMaxCameras: number;
  @Expose() public clearingValidMinutes: number;

  @Expose() public allowAlertApp: boolean;
  @Expose() public allowSsss: boolean;
  @Expose() public auditRetention: number;

  @Expose() public maxFailedLoginAttempts: number;
  @Expose() public lprDownMinutes: number;
  @Expose() public videoBitrate: number;
  @Expose() public temporaryMapViewMaxAge: number;
  @Expose() public videoFramerate: number;
  @Expose() public videoKeyframe: number;
  @Expose() public videoHeight: number;
  @Expose() public videoWidth: number;
  @Expose() public ssssPhoneNumber: number;
  @Expose() public ssssUniqueTipMinuteWindow: number;
  @Expose() public tipRetention: number;
  @Expose() public lprRetention: number;
  @Expose() public isSsssTipAnonymous: boolean;
  @Expose() public videoOverlayGeolocation: boolean;
  @Expose() public videoOverlayTimestamp: boolean;
  @Expose() public videoOverlayEmail: boolean;
  @Expose() public enableVideoWatermarking: boolean;
  @Expose() public enablePlaybackWatermarking: boolean;
  @Expose() public isDemo: boolean;
  @Expose() public unitDetailsBeta: boolean;
  @Expose() public skydioIncidentMarkerEnabled: boolean;
  @Expose() public temporaryGridMaxAge: number;
  @Expose() public cloudVideoTimeout: number;
  @Expose() public axonWarningEnabled: boolean;
  @Expose() public axonWarningTitle: string;
  @Expose() public axonWarningDescription: string;
  @Expose() public ecomIntegrationUseNewUserAttribution: boolean;

  @Expose()
  @Transform((value) => (value ? value.id : null))
  public streamAppliance: string;

  @Expose()
  @Transform((value) => (value ? value.id : null))
  public streamLiveApp: string;

  @Expose() public distanceFilter: number;
  @Expose() public locationShareFull: boolean;
  @Expose() public locationShareTeam: boolean;
  @Expose() public locationShareProximityRadius: number;

  @Expose() public incidentHeartbeatSeconds: number;
  @Expose() public skippedHeartbeatsAllowed: number;
  @Expose() public usersInactiveAfterDays: number;
  @Expose() public logoutTimeoutMinutes: number;

  @Expose()
  public complianceMessage: string;
  @Expose()
  public complianceTitle: string;
  @Expose()
  public isCompliance: boolean;

  @Expose() @ExposeRawData() public geometry: Point;

  @Expose() public mapStyle: string;
  @Expose({ name: 'slewToCue' }) public slewToCue: boolean;
  @Expose({ name: 'enableLiveMapNotification' }) public enableLiveMapNotification: boolean;
  @Expose() public mapBuildingLayer: boolean;
  @Expose() public mapDisplayCallSign: boolean;
  @Expose() public mapDisplayFullname: boolean;
  @Expose() public mapDisplayTeam: boolean;
  @Expose() public autoClosingAlerts: boolean;
  @Expose() public isMultipleUsersPerCallsignAllowed: boolean;

  @Expose() public measureUnits: string;

  @Expose() public downloadExpiresIn: number;
  @Expose() public alertClosingTimeout: number;

  @Expose() @ExposeRawData() public runtime: { API_KEY: string; CYPHER_KEY: string };

  @Expose() public previousPasswordsLimit: number;
  @Expose() public passwordExpirationPeriod: number;
  @Expose() public passwordMinimumLength: number;
  @Expose() public passwordMustContainUpper: boolean;
  @Expose() public passwordMustContainLower: boolean;
  @Expose() public passwordMustContainDigits: boolean;
  @Expose() public passwordMustContainSymbols: boolean;
  @Expose() public geospatialProvider: FsGeospatialProvider;

  @Expose()
  public envEnableLpr: boolean = true;

  @Expose()
  public envEnableFirebase: boolean = true;

  @Expose()
  public envEnableAbly: boolean = true;

  @Expose()
  public azurePubSubEnableModeWeb: LiveUpdateServiceType;

  @Expose()
  public alertsPipelineEnableMode: AlertsPipelineModeType;

  @Expose()
  public envEnableNotify: boolean = true;

  @Expose()
  public envIsControlled: boolean = false;

  @Expose() public defaultPanicCamera: {
    id: string;
    name: string;
  };

  @Expose() public applianceDashboard: boolean = false;

  @Expose() public lprOutpostEnabled: boolean = false;

  @Expose() public enableAppStore: boolean = false;

  @Expose()
  public presenceApiEnableMode: PresenceDataModeType = PresenceDataModeType.DEVICE_SEARCH_API;

  @Expose()
  public lprSearchExportEnabled: boolean = false;
  
  @Expose()
  public enableAdvancedDotsOnMapIcon: boolean = false;

  @Expose()
  @Default(false)
  public lprSharingManagementEnabled: boolean;

  @Expose()
  @Default(false)
  public sourceAndDeviceManagementEnabled: boolean;

  private _videoRatio: number;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }

  get coords(): { lng: number; lat: number } {
    return {
      lng: this.geometry.coordinates[0],
      lat: this.geometry.coordinates[1],
    };
  }

  get videoRatio(): number {
    if (!this._videoRatio) {
      this._videoRatio = this.videoHeight / this.videoWidth;
    }

    return this._videoRatio;
  }

  get tempGridMaxAgeDisplay(): string {
    return `${this.temporaryGridMaxAge / 60}h`;
  }
}
